import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {IconButton, Tooltip} from '@mui/material';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

/**
 * Drops every filter on the list view: the grid's column filters and the portal
 * level ones through the view controller, and the `useFilter` side panel, which the
 * view owns rather than the controller.
 *
 * Renders nothing for a controller that cannot clear, so a non-grid page can pass
 * whatever it has without guarding.
 */
export const ClearFiltersButton = (props)=> {
  const {viewController, extendedFilters} = props;

  /**
   * The grid's filter model is not React state, so the control would not know it had
   * gone stale. Listen to AG Grid instead of reading it on every render.
   */
  const [filtersActive, setFiltersActive] = useState(false);
  const gridApi = viewController?.grid?.api;
  const appliedExtendedFilters = extendedFilters?.applyFilters;

  useEffect(()=> {
    if(!gridApi || gridApi.destroyCalled) {
      setFiltersActive(Object.keys(appliedExtendedFilters || {}).length > 0);
      return undefined;
    }

    const refresh = ()=> {
      setFiltersActive(
        Object.keys(gridApi.getFilterModel() || {}).length > 0 ||
        Object.keys(appliedExtendedFilters || {}).length > 0
      );
    };

    refresh();
    gridApi.addEventListener('filterChanged', refresh);
    return ()=> {
      if(!gridApi.destroyCalled) gridApi.removeEventListener('filterChanged', refresh);
    };
  }, [gridApi, appliedExtendedFilters]);

  if(!viewController?.clearAllFilters) return null;

  const handleClear = ()=> {
    viewController.clearAllFilters();
    if(extendedFilters?.clearFilters) extendedFilters.clearFilters();
  };

  const label = filtersActive ? 'Clear all filters' : 'No filters applied';

  return (
    // A disabled IconButton does not emit pointer events, so the tooltip needs a
    // wrapper of its own or it never shows the "no filters" state.
    <Tooltip title={label}>
      <span>
        <IconButton
          aria-label={'clear filters'}
          color={'primary'}
          size={'large'}
          disabled={!filtersActive}
          onClick={handleClear}
        >
          <FilterAltOffIcon/>
        </IconButton>
      </span>
    </Tooltip>
  );
};

ClearFiltersButton.propTypes = {
  viewController: PropTypes.instanceOf(Object),
  extendedFilters: PropTypes.instanceOf(Object),
};

ClearFiltersButton.defaultProps = {
  viewController: undefined,
  extendedFilters: undefined,
};

export default ClearFiltersButton;
